import { Outlet, Link, useRouterState, useNavigate } from '@tanstack/react-router';
import { Menu, Bot, Users, Settings, FileText, ChevronLeft, ChevronRight, User, Image, Sparkles, Save } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useAuth, signOut } from '@/hooks/use-auth';
import { useQueryClient } from '@tanstack/react-query';
import { AIChatPanel } from '@/components/ai-chat-panel';
import { useSettingsContext } from '@/contexts/settings-context';

type NavItem = {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

const NAV_ITEMS: NavItem[] = [
  { href: '/agents/', label: 'Agents', icon: Bot },
  { href: '/users', label: 'Users', icon: Users },
  { href: '/blog', label: 'Blog', icon: FileText },
  { href: '/media/', label: 'Media', icon: Image },
  { href: '/settings', label: 'Settings', icon: Settings },
];

const ROUTE_UI_CONFIG: Record<
  string,
  {
    title: string;
    description: string;
    cta?: {
      label: string;
      to: string;
      icon: React.ComponentType<{ className?: string }>;
    };
  }
> = {
  '/agents/': {
    title: 'Agents Management',
    description: 'Theo dõi, duyệt và cập nhật các AI agents trên TrangVang.',
    cta: {
      label: 'Add New Agent',
      to: '/agents/new',
      icon: Bot,
    },
  },
  '/agents': {
    title: 'Agents Management',
    description: 'Theo dõi, duyệt và cập nhật các AI agents trên TrangVang.',
    cta: {
      label: 'Add New Agent',
      to: '/agents/new',
      icon: Bot,
    },
  },
  '/users': {
    title: 'Users Management',
    description: 'Quản lý thông tin và trạng thái của người dùng hệ thống.',
  },
  '/blog': {
    title: 'Blog Management',
    description: 'Soạn thảo, xuất bản và quản trị nội dung blog.',
    cta: {
      label: 'New Post',
      to: '/blog/new',
      icon: FileText,
    },
  },
  '/settings': {
    title: 'Settings',
    description: 'Điều chỉnh cấu hình và quyền truy cập cho TrangVang.AI.',
  },
  '/media/': {
    title: 'Media Library',
    description: 'Quản lý các file media và tài sản đã upload.',
  },
  '/': {
    title: 'Dashboard',
    description: 'Tổng quan dữ liệu và trạng thái hoạt động của TrangVang.AI.',
  },
};

function cn(...classes: (string | boolean | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

export function RootLayout() {
  const router = useRouterState();
  const pathname = router.location.pathname;
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  
  // Get settings context for save button
  const settingsContext = useSettingsContext();

  // Memoize callbacks to prevent infinite re-renders
  const handleToggleAiPanel = useCallback(() => {
    setAiPanelOpen((prev) => !prev);
  }, []);

  const handleCloseAiPanel = useCallback(() => {
    setAiPanelOpen(false);
  }, []);

  useEffect(() => {
    // Only redirect if we're sure there's no user (not loading and no user)
    // This prevents race conditions during navigation
    if (!loading && !user && pathname !== '/signin') {
      // Clear all queries when signing out
      queryClient.clear();
      navigate({ to: '/signin', replace: true });
    }
  }, [user, loading, pathname, navigate, queryClient]);

  const handleSignOut = async () => {
    await signOut();
    navigate({ to: '/signin' });
  };

  // Show loading state - but allow navigation during loading to prevent flicker
  // Only show loading spinner on initial load or when explicitly on signin page
  if (loading && pathname === '/signin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect to signin if not authenticated (handled by useEffect)
  // But don't block rendering during navigation transitions
  if (!loading && !user && pathname !== '/signin') {
    return null;
  }

  // Don't show layout on signin page
  if (pathname === '/signin') {
    return <Outlet />;
  }

  const segments = pathname.split('/').filter(Boolean);
  const basePath = segments.length ? `/${segments[0]}` : '/';
  const pageConfig = ROUTE_UI_CONFIG[basePath] ?? ROUTE_UI_CONFIG['/'];
  
  // Check if current route is settings page
  const isSettingsPage = pathname === '/settings' || pathname === '/settings/';
  
  // Check if current route is an edit page (has $id param)
  const isEditPage = (pathname.includes('/agents/') && segments.length >= 2 && segments[1] !== 'new') ||
                     (pathname.includes('/blog/') && segments.length >= 2 && segments[1] !== 'new');

  // Check if current route is add agent/page page
  const isAddAgentPage = pathname === '/agents/new' || pathname === '/agents/new/';
  const isAddBlogPage = pathname === '/blog/new' || pathname === '/blog/new/';

  // Check if current route is media page (has its own header)
  const isMediaPage = pathname.startsWith('/media');

  const Sidebar = ({ isCollapsed }: { isCollapsed: boolean }) => (
    <div
      className={cn(
        'flex h-full shrink-0 flex-col bg-white border-r border-gray-200',
        isCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Brand Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <Link to="/" className="flex items-center gap-3">
          {isCollapsed ? (
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
              <img src="/trangvang-ai-logo.png" alt="TrangVang.AI" width={20} height={20} className="rounded" />
            </div>
          ) : (
            <>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/90 shadow-sm">
                <img src="/trangvang-ai-logo.png" alt="TrangVang.AI" width={20} height={20} className="rounded" />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-semibold text-slate-900">TrangVang</span>
                <span className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">
                  admin
                </span>
              </div>
            </>
          )}
        </Link>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Toggle sidebar"
          onClick={() => setCollapsed((v) => !v)}
          className={cn(isCollapsed && 'mx-auto', 'text-gray-400 hover:text-gray-600')}
        >
          {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 py-4">
        <ul className="space-y-1 px-3">
          {NAV_ITEMS.map((item) => {
            const isActive = pathname?.startsWith(item.href) || false;
            const Icon = item.icon;
            return (
              <li key={item.href}>
                <Link
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-all',
                    'hover:bg-gray-50',
                    isActive
                      ? 'text-primary bg-yellow-50 border-l-2 border-primary'
                      : 'text-gray-700 border-l-2 border-transparent hover:border-primary'
                  )}
                  aria-label={item.label}
                >
                  <Icon className="h-4 w-4" />
                  <span className={cn('truncate', isCollapsed && 'hidden')}>{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User Profile Footer */}
      <div className="p-4 border-t border-gray-100">
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8 bg-gray-300">
            <AvatarFallback className="text-xs font-medium text-gray-600">
              {user?.email?.charAt(0).toUpperCase() || 'A'}
            </AvatarFallback>
          </Avatar>
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.email || 'admin@trangvang.com'}</p>
              <p className="text-xs text-gray-500 truncate">Admin User</p>
            </div>
          )}
          {!isCollapsed && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSignOut}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Sign out"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="hidden md:block">
        <Sidebar isCollapsed={collapsed} />
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header - Only show if not edit page, not add agent/blog page, and not media page */}
        {!isEditPage && !isAddAgentPage && !isAddBlogPage && !isMediaPage && (
          <header className="bg-white border-b border-gray-200 px-4 py-4 md:px-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3">
                <Sheet open={open} onOpenChange={setOpen}>
                  <SheetTrigger asChild className="md:hidden">
                    <Button
                      variant="outline"
                      size="icon"
                      aria-label="Open menu"
                      className="border-gray-200 text-gray-500 hover:bg-gray-50"
                    >
                      <Menu className="h-5 w-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="p-0 sm:max-w-xs">
                    <SheetHeader className="border-b border-gray-100 px-4 py-3">
                      <SheetTitle>Navigation</SheetTitle>
                    </SheetHeader>
                    <Sidebar isCollapsed={false} />
                  </SheetContent>
                </Sheet>
                <div>
                  <h1 className="text-2xl font-semibold text-slate-900">{pageConfig.title}</h1>
                  <p className="text-sm text-slate-500">{pageConfig.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Success/Error Messages for Settings */}
                {isSettingsPage && settingsContext.onSave && (
                  <>
                    {settingsContext.saveMessage && (
                      <div className="p-2 bg-green-50 border border-green-200 rounded-md">
                        <p className="text-xs text-green-700 font-medium">{settingsContext.saveMessage}</p>
                      </div>
                    )}
                    {settingsContext.saveError && (
                      <div className="p-2 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-xs text-red-600 font-medium">{settingsContext.saveError}</p>
                      </div>
                    )}
                  </>
                )}
                
                {/* Save Settings Button - Only show on settings page */}
                {isSettingsPage && settingsContext.onSave && (
                  <Button
                    type="button"
                    onClick={settingsContext.onSave}
                    disabled={settingsContext.isSaving}
                    className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-brand-yellow hover:bg-yellow-500 border border-brand-yellow shadow-sm transition-colors rounded-lg disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {settingsContext.isSaving ? 'Đang lưu...' : 'Save Changes'}
                  </Button>
                )}
                
                {/* AI Panel Toggle Button */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleToggleAiPanel}
                  className="border-gray-200 text-gray-500 hover:bg-gray-50"
                  aria-label={aiPanelOpen ? "Đóng AI Assistant" : "Mở AI Assistant"}
                >
                  <Sparkles className={cn(
                    "h-5 w-5 transition-transform duration-300",
                    aiPanelOpen && "text-primary"
                  )} />
                </Button>
                {pageConfig.cta ? (
                  <Link to={pageConfig.cta.to}>
                    <Button variant="yellow" className="px-4 py-2 rounded-md text-sm font-medium">
                      <pageConfig.cta.icon className="mr-2 h-4 w-4" />
                      {pageConfig.cta.label}
                    </Button>
                  </Link>
                ) : null}
              </div>
            </div>
          </header>
        )}

        {/* Content Area */}
        <main className={cn(
          "flex-1 overflow-auto bg-slate-50 transition-all duration-300",
          isEditPage || isAddAgentPage || isAddBlogPage || isMediaPage ? "" : "px-4 py-6 md:px-6"
        )}>
          {!isEditPage && !isAddAgentPage && !isAddBlogPage && !isMediaPage && (
            <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
              <Outlet />
            </div>
          )}
          {(isEditPage || isAddAgentPage || isAddBlogPage || isMediaPage) && <Outlet />}
        </main>

        {/* Floating toggle button for edit/add pages */}
        {(isEditPage || isAddAgentPage || isAddBlogPage || isMediaPage) && (
          <Button
            variant="outline"
            size="icon"
            onClick={handleToggleAiPanel}
            className={cn(
              "fixed bottom-6 right-6 z-40 h-12 w-12 rounded-full shadow-lg transition-all duration-300",
              "border-gray-200 bg-white hover:bg-gray-50",
              aiPanelOpen && "bg-primary/10 border-primary"
            )}
            aria-label={aiPanelOpen ? "Đóng AI Assistant" : "Mở AI Assistant"}
          >
            <Sparkles className={cn(
              "h-5 w-5 transition-transform duration-300",
              aiPanelOpen && "text-primary"
            )} />
          </Button>
        )}
      </div>

      {/* AI Chat Panel - Full height column, same level as sidebar */}
      {aiPanelOpen && (
        <>
          {/* Desktop: Full height column */}
          <aside className="hidden lg:block w-[400px] shrink-0 h-full border-l border-gray-200 bg-white overflow-hidden">
            <AIChatPanel isOpen={aiPanelOpen} onClose={handleCloseAiPanel} />
          </aside>
          {/* Mobile: Overlay */}
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <div className="flex-1 bg-black/50" onClick={handleCloseAiPanel} aria-hidden="true" />
            <aside className="w-[85vw] max-w-sm bg-white shadow-xl overflow-hidden">
              <AIChatPanel isOpen={aiPanelOpen} onClose={handleCloseAiPanel} />
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
